import React from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { OPEN, RESOLVED } from '../../common/constants';
import { useRelatedIssuesForIssue, useRelatedIssuesFeedbackCollector } from '../../services';
import messages from './messages';

export const TEST_ID_BASE = 'smart-related-issues-panel.ui.feedback-sentiment.smart';

export const RelatedIssuesFeedbackSentiment = ({
	sessionId,
	hasClickedSentiment,
	setHasClickedSentiment,
	openIssuesDistance,
	resolvedIssuesDistance,
	openIssuesCrossEncoderScore,
	resolvedIssuesCrossEncoderScore,
}: {
	sessionId: string;
	hasClickedSentiment: boolean;
	setHasClickedSentiment: React.Dispatch<React.SetStateAction<boolean>>;
	openIssuesDistance: Array<number>;
	resolvedIssuesDistance: Array<number>;
	openIssuesCrossEncoderScore: Array<number>;
	resolvedIssuesCrossEncoderScore: Array<number>;
}) => {
	const [{ isLoading, error, data }] = useRelatedIssuesForIssue();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { openRelatedIssuesFeedback } = useRelatedIssuesFeedbackCollector(sessionId);
	const { formatMessage } = useIntl();

	// Only show sentiment buttons when the results have loaded without error AND there are >0 results.
	const totalRelatedIssueCount: number = (data[OPEN]?.length ?? 0) + (data[RESOLVED]?.length ?? 0);
	if (isLoading || error || totalRelatedIssueCount === 0) {
		return null;
	}

	if (hasClickedSentiment) {
		return (
			<SentimentContainer>
				{formatMessage(messages.thanksForYourFeedback)}{' '}
				{formatMessage(messages.toImproveFutureResultsWithPlaceholder, {
					tellUsMore: (
						<Button
							appearance="link"
							spacing="none"
							data-testId="smart-related-issues-panel.ui.feedback-sentiment.smart.feedback-collector"
							onClick={openRelatedIssuesFeedback}
							interactionName="auto-generated.jira-smart-related-issues-panel.ui.feedback-sentiment.button"
						>
							{formatMessage(messages.tellUsMore)}
						</Button>
					),
				})}
			</SentimentContainer>
		);
	}

	const onButtonClick = (
		wereResultsRelevant: boolean,
		openIssuesDistances: Array<number>,
		resolvedIssuesDistances: Array<number>,
		openIssuesCrossEncoderScores: Array<number>,
		resolvedIssuesCrossEncoderScores: Array<number>,
	) => {
		setHasClickedSentiment(true);
		fireUIAnalytics(createAnalyticsEvent({}), 'relatedIssuesSentiment clicked', {
			wereResultsRelevant,
			openIssuesDistances,
			resolvedIssuesDistances,
			openIssuesCrossEncoderScores,
			resolvedIssuesCrossEncoderScores,
		});
	};

	return (
		<SentimentContainer>
			<span>{formatMessage(messages.areTheseRelevant)}</span>
			{(['yes', 'no'] as const).map((buttonId) => (
				<Button
					key={buttonId}
					spacing="compact"
					onClick={() =>
						onButtonClick(
							buttonId === 'yes',
							openIssuesDistance,
							resolvedIssuesDistance,
							openIssuesCrossEncoderScore,
							resolvedIssuesCrossEncoderScore,
						)
					}
					data-testId={`${TEST_ID_BASE}.${buttonId}`}
					aria-label={formatMessage(messages[`${buttonId}AriaLabel`])}
					interactionName="auto-generated.jira-smart-related-issues-panel.ui.feedback-sentiment.button1"
				>
					{formatMessage(messages[buttonId])}
				</Button>
			))}
		</SentimentContainer>
	);
};

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const SentimentContainer = styled.p({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minHeight: `${gridSize * 3}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > button': {
		marginLeft: token('space.050', '4px'),
		marginRight: token('space.050', '4px'),
	},
});
